// Run now: Live or Test, which jobs, and what pressing it will do, before anything is queued.
//
// Jobs default to the ones due today and not completed today (decision 1); "all jobs" is an
// explicit choice, and the page always names the jobs it wants. Test (a scratch run: test
// storage only, never the day's run) is offered only for logins on an office desk; a cluster
// login's tests happen on the dev desk (POLICY rule 12). "This will…" comes from the API's
// run-preview when it has one, else from what /logins already says. A refusal shows here,
// before queuing; the button names the action.

import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { getRunPreview, runNow } from './api';
import { defaultRunJobs, deskLabel, isOffice, jobRows, prodName, loginDesk, previewMissing, runButtonText, runPreview } from './deskModel';
import { useTones } from './tones';

const jobSub = (j) => {
    if (j.dueToday) return j.completedToday ? 'Due today, completed. A rerun fetches only files that did not land today.' : 'Due today, not completed.';
    return `Not due today (${j.daysText}).${j.doneThisWeek ? ' Done this week.' : ''} Ticking it runs it in full.`;
};

// login: the row, or null. data: the /logins payload (office_desks).
const RunNowDialog = ({ login, data, onClose, onDone }) => {
    const t = useTones();
    const [scratch, setScratch] = useState(false);
    const [picked, setPicked] = useState([]);
    const [preview, setPreview] = useState(null); // null: none (yet, or the API has no preview)
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const office = login ? isOffice(login, data) : false;
    const jobs = useMemo(() => (login ? jobRows(login) : []), [login]);

    useEffect(() => {
        if (!login) return;
        setScratch(false); setError(null); setPreview(null);
        setPicked(defaultRunJobs(login));
    }, [login]);

    // The preview follows the selection; a 404 (older API) leaves the fallback text.
    useEffect(() => {
        if (!login || !picked.length) { setPreview(null); return undefined; }
        let live = true;
        const timer = setTimeout(() => {
            getRunPreview(login.id, picked, scratch)
                .then((p) => { if (live) setPreview(p); })
                .catch(() => { if (live) setPreview(null); }); // 404 on an older API: the fallback text stands
        }, 250);
        return () => { live = false; clearTimeout(timer); };
    }, [login, picked, scratch]);

    if (!login) return <Dialog open={false} />;
    const client = [...new Set((login.jobs || []).map((j) => j.client).filter(Boolean))].join(', ');
    const { lines, refused } = runPreview({ row: login, picked, scratch, preview, clientName: client });
    const names = picked.map((jt) => jobs.find((j) => j.job_type === jt)?.name || jt);
    const label = runButtonText({ scratch, names, total: jobs.length, missing: previewMissing(preview, picked) });
    const toggle = (jt) => setPicked((cur) => (cur.includes(jt) ? cur.filter((x) => x !== jt) : jobs.map((j) => j.job_type).filter((x) => x === jt || cur.includes(x))));
    const all = picked.length === jobs.length;

    const submit = async () => {
        setBusy(true); setError(null);
        try {
            const res = await runNow(login.id, { jobTypes: picked, scratch });
            onDone(res?.message || `Queued: ${label}`);
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle className="d-font">
                Run {login.id} now
                <Typography className="d-font" sx={{ fontSize: 12, color: t.muted }}>
                    {deskLabel(prodName(loginDesk(login)))}{office ? ' (office Mac)' : ''} picks it up within about 30 seconds if idle.
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
                    <ToggleButtonGroup exclusive size="small" value={scratch ? 'test' : 'live'} aria-label="Run kind"
                        onChange={(e, v) => { if (v) setScratch(v === 'test'); }}>
                        <ToggleButton value="live" sx={{ textTransform: 'none', px: 2, py: 0.25 }}>Live</ToggleButton>
                        <Tooltip title={office ? '' : 'Test runs for cluster logins happen on the dev desk (python -m Desk.laptop try)'}>
                            <span>
                                <ToggleButton value="test" disabled={!office} selected={scratch}
                                    onChange={() => setScratch(true)} sx={{ textTransform: 'none', px: 2, py: 0.25 }}>
                                    Test
                                </ToggleButton>
                            </span>
                        </Tooltip>
                    </ToggleButtonGroup>
                    <Typography className="d-font" sx={{ fontSize: 12, color: t.muted }}>
                        {scratch ? 'Test writes to test storage only. Nothing reaches the client or the transform.'
                            : `Live writes to ${client || 'the client'}'s storage and runs the transform.`}
                        {!office && ' Test runs: dev desk (Desk.laptop try).'}
                    </Typography>
                </Box>

                <Box sx={{ border: '1px solid var(--color-border, #e0e0e0)', borderRadius: 1, px: 1.5, py: 0.5, mb: 1.5 }}>
                    {jobs.map((j) => (
                        <FormControlLabel key={j.job_type} sx={{ display: 'flex', alignItems: 'flex-start', my: 0.5, mr: 0 }}
                            control={<Checkbox size="small" checked={picked.includes(j.job_type)} onChange={() => toggle(j.job_type)} sx={{ pt: 0.25 }} />}
                            label={(
                                <Box className="d-font">
                                    <Typography component="span" className="d-font" sx={{ fontSize: 13, fontWeight: 600 }}>{j.name}</Typography>
                                    <Typography className="d-font" sx={{ fontSize: 12, color: t.muted }}>{jobSub(j)}</Typography>
                                </Box>
                            )} />
                    ))}
                    {jobs.length > 1 && (
                        <Button size="small" sx={{ textTransform: 'none', mb: 0.5 }}
                            onClick={() => setPicked(all ? defaultRunJobs(login) : jobs.map((j) => j.job_type))}>
                            {all ? 'Back to the due jobs' : `All ${jobs.length} jobs`}
                        </Button>
                    )}
                </Box>

                {refused ? (
                    <Alert severity="error" data-refused><b>Refused before queuing:</b> {refused}</Alert>
                ) : (
                    <Box data-will className="d-font" sx={{ fontSize: 13, p: 1.25, borderRadius: 1, bgcolor: t.dark ? 'action.hover' : '#f5f6f8' }}>
                        <b>This will:</b>
                        {lines.map((l, i) => <Box key={i}>· {l}</Box>)}
                    </Box>
                )}
                {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ '& .MuiButton-root': { textTransform: 'none' } }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={busy || !picked.length || Boolean(refused)} onClick={submit}>{label}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default RunNowDialog;
